import Class from "./Class";
import AST from "./AST";
import rulesAST from "./rulesAST";
import {escapeRegExp} from "./parser";
import number from "./formats/number";
import ipv4 from "./formats/ipv4";

const RegExpVisitor = new Class({
    Extends: AST.Visitor,

    "visit:regexp:empty": function(node) {
        return "";
    },

    "visit:regexp:char": function(node) {
        return node.char;
    },

    "visit:regexp:class": function(node) {
        const ranges = node.ranges.map(function(range) {
            if (range.start === range.end) {
                return escapeRegExp(range.start);
            } else {
                return escapeRegExp(range.start) + "-" + escapeRegExp(range.end);
            }
        });

        return "[" + (node.reversed ? "^" : "") + ranges.join("") + "]";
    },

    "visit:regexp:anychar": function(node) {
        return ".";
    },

    "visit:regexp:start": function(node) {
        return "^";
    },

    "visit:regexp:end": function(node) {
        return "$";
    },

    "visit:regexp:repeat": function(node) {
        const left = String(node.min);
        const right = node.max === Infinity ? "" : String(node.max);
        return node.value + "{" + left + "," + right + "}";
    },

    "visit:regexp:group": function(node) {
        return "(" + node.value + ")";
    },

    "visit:regexp:sequence": function(node) {
        return node.values.join("");
    },

    "visit:regexp:or": function(node) {
        return node.values.join("|");
    }
});

const MatcherVisitor = new Class({
    Extends: AST.Visitor,

    _rexVisitor: new RegExpVisitor(),

    _invert: function(matcherFunc) {
        return function() {
            return !matcherFunc.apply(null, arguments);
        };
    },

    "visit:and": function(node) {
        const rules = node.values;
        const length = rules.length;

        return function(item) {
            for (let i = 0; i < length; i++) {
                if (!rules[i](item)) {
                    return false;
                }
            }
            return true;
        };
    },

    "visit:or": function(node) {
        const rules = node.values;
        const length = rules.length;

        return function(item) {
            for (let i = 0; i < length; i++) {
                if (rules[i](item)) {
                    return true;
                }
            }
            return false;
        };
    },

    "visit:no": function(node) {
        const rule = node.value;

        return function(item) {
            return !rule(item);
        };
    },

    "visit:<": function(node) {
        const matcher = node.left.eventMatcher(function(value) {
            const result = node.right.cmp(value);
            return result !== null && result < 0;
        });

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:>": function(node) {
        const matcher = node.left.eventMatcher(function(value) {
            const result = node.right.cmp(value);
            return result !== null && result > 0;
        });

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:<=": function(node) {
        const matcher = node.left.eventMatcher(function(value) {
            const result = node.right.cmp(value);
            return result !== null && result <= 0;
        });

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:>=": function(node) {
        const matcher = node.left.eventMatcher(function(value) {
            const result = node.right.cmp(value);
            return result !== null && result >= 0;
        });

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:=": function(node) {
        const matcher = node.left.eventMatcher(node.right.valueMatcher());

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:!=": function(node) {
        const matcher = node.left.eventMatcher(this._invert(node.right.valueMatcher()));

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:in": function(node) {
        const matcher = node.left.eventMatcher(node.right.groupMatcher());

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:not in": function(node) {
        const matcher = node.left.eventMatcher(this._invert(node.right.groupMatcher()));

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:fuzzy": function(node) {
        const matcher = node.value.fuzzyMatcher();

        return function(item) {
            return matcher(item.event);
        };
    },

    "visit:string": function(node) {
        const string = node.string;

        return {
            "eventMatcher": function(valueMatcher) {
                const nonMatch = {};
                const mapFilter = {
                    "filter": valueMatcher
                };

                return function(event) {
                    return event.value(string, nonMatch, mapFilter) !== nonMatch;
                };
            },

            "valueMatcher": function() {
                return function(value) {
                    return value === string;
                };
            },

            "fuzzyMatcher": function() {
                const rex = new RegExp(escapeRegExp(string), "i");
                const test = function(value, key) {
                    return rex.test(value) || rex.test(key);
                };

                return function(event) {
                    return event.some(test);
                };
            }
        };
    },

    "visit:*": function(node) {
        return {
            "eventMatcher": function(valueMatcher) {
                const test = function(value, key) {
                    return valueMatcher(value);
                };

                return function(event) {
                    return event.some(test);
                };
            },

            "valueMatcher": function() {
                return function(value) {
                    return true;
                };
            },

            "fuzzyMatcher": function() {
                return function(event) {
                    return true;
                };
            }
        };
    },

    "visit:regexp": function(node) {
        const pattern = this._rexVisitor.visit(node.ast);
        const rex = new RegExp(pattern, node.ignoreCase ? "i" : "");

        return {
            "valueMatcher": function() {
                return function(value) {
                    return rex.test(value);
                };
            },

            "fuzzyMatcher": function() {
                const test = function(value, key) {
                    return rex.test(value) || rex.test(key);
                };

                return function(event) {
                    return event.some(test);
                };
            }
        };
    },

    "visit:number": function(node) {
        const value = node.number;

        const equals = function(string) {
            return number.parse(string) === value;
        };

        const cmp = function(string) {
            const other = number.parse(string);
            if (other === null) {
                return null;
            }
            if (other === value) {
                return 0;
            }
            return other < value ? -1 : 1;
        };

        return {
            "cmp": cmp,

            "valueMatcher": function() {
                return equals;
            },

            "fuzzyMatcher": function() {
                return function(event) {
                    return event.some(equals);
                };
            }
        };
    },

    "visit:cidr": function(node) {
        const cidr = node.cidr;

        const match = function(value) {
            const other = ipv4.parseCIDR(value);
            if (other === null) {
                return false;
            }
            return cidr.contains(other);
        };

        return {
            "fuzzyMatcher": function() {
                return function(event) {
                    return event.some(match);
                };
            },

            "groupMatcher": function() {
                return match;
            }
        };
    }
});

const visitor = new MatcherVisitor();

export default {
    "parse": function(_string) {
        const string = (_string || "").trim();
        if (!string) {
            return function(_) {
                return true;
            };
        }

        const parsed = rulesAST.parse(string);
        if (parsed === null) {
            return null;
        }
        return visitor.visit(parsed);
    }
};



// WEBPACK FOOTER //
// ./src/abusesa-js/rules.js